import { clsx } from 'clsx';
import { useCallback, useState } from 'react';
import { Document, DocumentProps, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Text from '~/components/typographies/Text';
import IconArrowLeft from '~/icons/icon-arrow-left.svg?react';
import IconArrowRight from '~/icons/icon-arrow-right.svg?react';
import IconZoomIn from '~/icons/icon-zoom-in.svg?react';
import IconZoomOut from '~/icons/icon-zoom-out.svg?react';
import styles from './PdfViewer.module.scss';
import IconButton from './buttons/NewIconButton';
import { HStack } from './layout/Stack';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

interface PdfControlsProps {
  numPages: number;
  pageNumber: number;
  prevPage: () => void;
  nextPage: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

const PdfControls = ({ numPages, pageNumber, ...controls }: PdfControlsProps) => {
  return (
    <HStack gap={1} justify="space-between" align="center" className={styles.legend}>
      {numPages > 1 && (
        <HStack justify="space-between" align="center" className={styles.moveButtons} gap={1}>
          <IconButton Icon={IconArrowLeft} onClick={controls.prevPage} disabled={pageNumber === 1} variant="ghost" />
          <Text variant="body2" weight="regular">
            {pageNumber}/{numPages}
          </Text>
          <IconButton
            Icon={IconArrowRight}
            onClick={controls.nextPage}
            disabled={pageNumber === numPages}
            variant="ghost"
          />
        </HStack>
      )}

      <HStack justify="space-between" align="center">
        <IconButton Icon={IconZoomOut} onClick={controls.zoomOut} variant="ghost" />
        <IconButton Icon={IconZoomIn} onClick={controls.zoomIn} variant="ghost" />
      </HStack>
    </HStack>
  );
};

interface Props {
  file: DocumentProps['file'];
  className?: string;
  width?: number;
  height?: number;
}

export const PdfViewer = ({ file, className, width, height }: Props) => {
  const [numPages, setNumPages] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => setNumPages(numPages);

  const prevPage = useCallback(() => setPageNumber((current) => Math.max(1, current - 1)), [setPageNumber]);
  const nextPage = useCallback(
    () => setPageNumber((current) => Math.min(numPages, current + 1)),
    [numPages, setPageNumber],
  );
  const zoomIn = useCallback(() => setScale((current) => current * 1.1), [setScale]);
  const zoomOut = useCallback(() => setScale((current) => current * 0.9), [setScale]);

  return (
    <div className={clsx(className, styles.document)}>
      <Document file={file} onLoadSuccess={onDocumentLoadSuccess} className={styles.page}>
        <Page
          pageNumber={pageNumber}
          renderAnnotationLayer={false}
          renderTextLayer={true}
          width={width}
          height={height}
          scale={scale}
        />
      </Document>

      <PdfControls
        nextPage={nextPage}
        prevPage={prevPage}
        pageNumber={pageNumber}
        zoomIn={zoomIn}
        zoomOut={zoomOut}
        numPages={numPages}
      />
    </div>
  );
};
